import React, { useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { CivSelector } from '../../components/CivSelector';
import { MapSelector } from '../../components/MapSelector';
import { mergeCivDraftView, mergeMapDraftView } from '../../helpers/mergeDraftView';
import { useSocketState } from '../../helpers/useSocket';
import Countdown from 'react-countdown';
import { countdownRenderer } from '../../helpers/countdownRenderer';

function DraftDashboard() {
    const { socket } = useOutletContext()
    const [match] = useSocketState('Match');
    const [civDraft] = useSocketState('CivDraft');
    const [mapDraft] = useSocketState('MapDraft');
    const [civDraftUrl, setCivDraftUrl] = useState(null);
    const [mapDraftUrl, setMapDraftUrl] = useState(null);

    // Delay load draftId
    if (civDraftUrl === null && civDraft?.draftId) {
        setCivDraftUrl(civDraft.draftId);
    }
    if (mapDraftUrl === null && mapDraft?.draftId) {
        setMapDraftUrl(mapDraft.draftId);
    }

    const civDraftView = civDraft && match ? mergeCivDraftView(civDraft?.view || {}, match.games, match.matchRedraftAfter) : {};
    const mapDraftView = mapDraft && match ? mergeMapDraftView(mapDraft?.view || {}, match.games, 0) : {};

    const gameOptions = ['Bo1', 'Bo3', 'Bo5', 'Bo7', 'Bo9', 'Bo11', 'Pa2', 'Pa3', 'Pa5', 'Bo1 - 2v2', 'Bo3 - 2v2', 'Bo5 - 2v2', 'Bo7 - 2v2']
    const gameStateOptions = ['left', 'right', 'upcoming', 'tbd']
    const statusPresets = [{ id: 'drafting', value: 'Players are drafting' }, { id: 'beginning', value: 'Game is about to begin...' }, { id: 'finished', value: 'Thank you for your support! ♥' }, { id: 'interview', value: 'Interview is about to begin...' }, { id: 'hide', value: '' }]

    const expectedNumGames = parseInt(match?.matchType.match(/\d+/)?.at(0) || '0');
    const hasRedraft = expectedNumGames >= 7;

    function handleImportCivDraft() {
        const draftId = civDraftUrl.match(/[a-zA-Z0-9]{5,}$/)?.at(0)
        socket.emit('setCivDraftId', draftId);
        if (draftId) {
            // Auto set redraft, just for this tourney
            if (hasRedraft) {
                const nextGameIndex = match.games.findIndex(v => v.state === 'tbd' || v.state === 'upcoming');
                if (nextGameIndex === 4) {
                    socket.emit('modifyMatch', { matchRedraftAfter: 4 });
                } else if (nextGameIndex === 0) {
                    socket.emit('modifyMatch', { matchRedraftAfter: 0 });
                }
            } else if (match.matchRedraftAfter !== 0) {
                socket.emit('modifyMatch', { matchRedraftAfter: 0 });
            }
        }
    }

    function handleImportMapDraft() {
        const draftId = mapDraftUrl.match(/[a-zA-Z0-9]{5,}$/)?.at(0);
        socket.emit('setMapDraftId', draftId);
    }

    function handleSwapCivDraftHost() {
        socket.emit('swapCivDraftPlayers');
    }

    function handleSwapMapDraftHost() {
        socket.emit('swapMapDraftPlayers');
    }

    function handleCivDraftUrlChange(el) {
        setCivDraftUrl(el.target.value);
    }

    function handleMapDraftUrlChange(el) {
        setMapDraftUrl(el.target.value);
    }

    function handleMatchTypeChange(el) {
        socket.emit('modifyMatch', { matchType: el.target.value });
    }

    function handleMatchRedraftAfterChange(el) {
        socket.emit('modifyMatch', { matchRedraftAfter: el.target.value });
    }

    function modifyGame(gameIndex, patch) {
        let newGames = [...match.games];
        newGames[gameIndex] = { ...newGames[gameIndex], ...patch };
        socket.emit('modifyMatch', { games: newGames });
        console.log(newGames)
    }

    function getFutureTimestamp(seconds) {
        if (seconds === null) {
            socket.emit('modifyMatch', { countdownTimer: null })
            return
        }
        var date = new Date().getTime() + seconds * 1000;
        socket.emit('modifyMatch', { countdownTimer: date })
    }

    function handleMatchStatusChange(el) {
        const text = statusPresets.find(item => item.id === el.target.value)
        socket.emit('modifyMatch', { matchStatus: text.value })
    }

    function handleRoundInfo(el) {
        el.preventDefault()
        socket.emit('modifyMatch', { roundInfo: el.target.roundInfo.value })
    }

    function copyNamesFromCivDraft() {
        socket.emit('modifyLeftPlayer', {name: civDraftView.hostName});
        socket.emit('modifyRightPlayer', {name: civDraftView.guestName});
    }
    
    function copyNamesFromMapDraft() {
        socket.emit('modifyLeftPlayer', {name: mapDraftView.hostName});
        socket.emit('modifyRightPlayer', {name: mapDraftView.guestName});
    }

    return (
        <>
            <div className='flex flex-row pt-4'>
                {
                    match?.games &&
                    <div className='flex flex-col'>
                        <div className='flex flex-row'>
                            <form className='flex flex-col' onSubmit={handleRoundInfo}>
                                <label>Round Info</label>
                                <input type="text" id="roundInfo" name="roundInfo" className='w-56 mr-8' defaultValue={match.roundInfo} />
                                <button className='w-24 mt-1' type='submit'>Submit</button>
                            </form>
                            <div className='flex flex-col'>
                                <label>Status</label>
                                <input type="text" id="draftUrl" name="draftUrl" className='w-56 mr-8' value={match.matchStatus} />
                                <select value={statusPresets.find(item => item.value === match.matchStatus).id} onChange={handleMatchStatusChange} className='border-solid border-[0.5px] h-8 w-56'>{statusPresets.map(option => <option value={option.id}>{option.value}</option>)}</select>
                            </div>
                            <div className='flex flex-col'>
                                <label>Timer</label>
                                <input type="text" id="countdownTimer" name="countdownTimer" className='w-56 mr-8' value={match?.countdownTimer} />
                                {match.countdownTimer ?
                                    <Countdown date={match.countdownTimer} renderer={countdownRenderer} />
                                    :
                                    <>00:00</>
                                }
                                <div>
                                    <button onClick={() => getFutureTimestamp(0)} className='w-12 h-8 p-0 bg-green-600 hover:bg-green-800 m-1'>0 min</button>
                                    <button onClick={() => getFutureTimestamp(60)} className='w-12 h-8 p-0 bg-green-600 hover:bg-green-800 m-1'>1 min</button>
                                    <button onClick={() => getFutureTimestamp(120)} className='w-12 h-8 p-0 bg-green-600 hover:bg-green-800 m-1'>2 min</button>
                                    <button onClick={() => getFutureTimestamp(180)} className='w-12 h-8 p-0 bg-green-600 hover:bg-green-800 m-1'>3 min</button>
                                    <button onClick={() => getFutureTimestamp(240)} className='w-12 h-8 p-0 bg-green-600 hover:bg-green-800 m-1'>4 min</button>
                                    <button onClick={() => getFutureTimestamp(300)} className='w-12 h-8 p-0 bg-green-600 hover:bg-green-800 m-1'>5 min</button>
                                    <button onClick={() => getFutureTimestamp(null)} className='w-12 h-8 p-0 bg-green-600 hover:bg-green-800 m-1'>HIDE</button>
                                </div>
                            </div>
                        </div>
                        <div className="flex flex-row">
                            <div className='flex flex-col'>
                                <label>Map Draft</label>
                                <div className='flex flex-row'>
                                    <input type="text" id="mapDraftUrl" name="mapDraftUrl" className='w-56 mr-8' value={mapDraftUrl} onChange={handleMapDraftUrlChange} />
                                    <button className='h-8 my-auto mr-2' onClick={handleImportMapDraft}>Import/Connect</button>
                                    <button className='h-8 my-auto mr-4 w-28 text-xs' onClick={copyNamesFromMapDraft}>Copy Names</button>
                                </div>
                                <label>Civ Draft</label>
                                <div className='flex flex-row'>
                                    <input type="text" id="civDraftUrl" name="civDraftUrl" className='w-56 mr-8' value={civDraftUrl} onChange={handleCivDraftUrlChange} />
                                    <button className='h-8 my-auto mr-2 ' onClick={handleImportCivDraft}>Import/Connect</button>
                                    <button className='h-8 my-auto mr-4 w-28 text-xs' onClick={copyNamesFromCivDraft}>Copy Names</button>
                                </div>
                            </div>
                            <div className='flex flex-col'>
                            </div>
                            <div className='flex flex-col'>
                                <label>Format</label>
                                <select value={match.matchType} className='border-solid border-[0.5px] h-8 w-16' onChange={handleMatchTypeChange}>{gameOptions.map(option => <option value={option}>{option}</option>)}</select>
                            </div>
                            {
                                (hasRedraft || !!match.matchRedraftAfter) &&
                                <div className='flex flex-col'>
                                    <label>Redraft Offset</label>
                                    <input type="number" id="matchRedraftAfter" name="matchRedraftAfter" value={match.matchRedraftAfter || 0} className='border-solid border-[0.5px] h-8 w-16' onChange={handleMatchRedraftAfterChange} />
                                </div>
                            }
                        </div>
                        {civDraftView?.host &&
                            <div className="mt-8 flex flex-row">
                                <div className='px-2 border-black' style={{ borderRightWidth: "3px" }}>
                                    <div className='pb-2'>
                                        Name: {civDraftView.hostName}

                                        <button className='h-6 py-0 my-auto float-right' onClick={handleSwapCivDraftHost}>Swap</button>
                                    </div>
                                    {civDraftView.host.map(draftOption => <CivSelector civ={draftOption.option} state={draftOption.action} leftSide={true} allowEmpty={true} />)}
                                    {/* <div className='flex flex-row justify-center'>
                                    <button className='bg-green-500'>+</button>
                                    </div> */}
                                </div>

                                <div className='px-2'>
                                    <div className='pb-2'>Name: {civDraftView.guestName}</div>
                                    {civDraftView.guest.map(draftOption => <CivSelector civ={draftOption.option} state={draftOption.action} leftSide={false} allowEmpty={true} />)}
                                </div>
                            </div>
                        }
                        {mapDraftView?.host &&
                            <div className="mt-8 flex flex-row">
                                <div className='px-2 border-black' style={{ borderRightWidth: "3px" }}>
                                    <div className='pb-2'>
                                        Name: {mapDraftView.hostName}

                                        <button className='h-6 py-0 my-auto float-right' onClick={handleSwapMapDraftHost}>Swap</button>
                                    </div>
                                    {mapDraftView.host.map(draftOption => <MapSelector map={draftOption.option} state={draftOption.action} leftSide={true} allowEmpty={true} iconMode='small' />)}
                                    {/* <div className='flex flex-row justify-center'>
                                    <button className='bg-green-500'>+</button>
                                    </div> */}
                                </div>

                                <div className='px-2'>
                                    <div className='pb-2'>Name: {mapDraftView.guestName}</div>
                                    {mapDraftView.guest.map(draftOption => <MapSelector map={draftOption.option} state={draftOption.action} leftSide={false} allowEmpty={true} iconMode='small' />)}
                                </div>
                            </div>
                        }
                    </div>
                }

                {
                    match &&
                    <div className='flex flex-col mt-20 ml-8'>
                        {match?.games.map((game, gameIndex) =>
                            <div className='flex flex-row pt-4'>
                                <div className='flex flex-col'>
                                    {match.matchType.includes("2v2") ? <>
                                        <CivSelector civ={game.leftCivs[0]} leftSide={true} allowEmpty={true} onChange={civ => modifyGame(gameIndex, { leftCivs: [civ, game.leftCivs[1]] })} />
                                        <CivSelector civ={game.leftCivs[0]} leftSide={true} allowEmpty={true} onChange={civ => modifyGame(gameIndex, { leftCivs: [game.leftCivs[0], civ] })} />
                                    </> :
                                        <CivSelector civ={game.leftCivs[0]} leftSide={true} allowEmpty={true} onChange={civ => modifyGame(gameIndex, { leftCivs: [civ] })} />
                                    }

                                </div>
                                <div className='flex flex-col'>
                                    <MapSelector map={game.map} allowEmpty={true} onChange={map => modifyGame(gameIndex, { map: map })} />
                                    <select value={game.state} className='border-solid border-[0.5px] h-8 w-24 mx-auto' onChange={el => modifyGame(gameIndex, { state: el.target.value })}>
                                        {gameStateOptions.map(option => <option value={option}>{option}</option>)}
                                    </select>
                                </div>
                                <div className='flex flex-col'>
                                    {match.matchType.includes("2v2") ? <>
                                        <CivSelector civ={game.rightCivs[0]} leftSide={false} allowEmpty={true} onChange={civ => modifyGame(gameIndex, { rightCivs: [civ, game.rightCivs[1]] })} />
                                        <CivSelector civ={game.rightCivs[1]} leftSide={false} allowEmpty={true} onChange={civ => modifyGame(gameIndex, { rightCivs: [game.rightCivs[0], civ] })} />
                                    </> :
                                        <CivSelector civ={game.rightCivs[0]} leftSide={false} allowEmpty={true} onChange={civ => modifyGame(gameIndex, { rightCivs: [civ] })} />
                                    }
                                </div>
                            </div>
                        )}
                        <div className='flex flex-row justify-center pt-4'>
                            <button className='bg-green-500 hover:bg-green-700'>+</button>
                        </div>
                    </div>
                }
            </div >
        </>
    )
}

export default DraftDashboard;
